// RITA-1515 (Nutzungserfassung Phase 3a, Epic RITA-1496) — Ladeleistung über web-vitals.
//
// Gemessen werden die vier Kennzahlen des Backend-Vertrags (batchSchema, Ereignisart `vital`):
// LCP (wann stand der Hauptinhalt?), INP (wie träge reagiert die Bedienung?), CLS (wie sehr
// springt das Layout?) und TTFB (wie lange braucht der Server bis zum ersten Byte?).
//
// EIN BEOBACHTERSATZ JE SEITENLADUNG (E2): `createWebVitalsBridge` registriert onLCP/onINP/
// onCLS/onTTFB beim ERSTEN attach und danach nie wieder. Grund: web-vitals meldet einem neu
// registrierten Beobachter die bereits gepufferten Einträge erneut — Abmelden und wieder
// Anmelden im selben Tab würde dieselbe Messung ein zweites Mal zählen. `detach` löst deshalb
// nur den Handler, nicht die Beobachter.
//
// ZUORDNUNG ZUR ROUTE (E3): Der Meldezeitpunkt taugt nicht als Route — er liegt oft Seiten
// später. Maßgeblich ist der Zeitpunkt des Performance-Eintrags, auf dem der Wert beruht:
// `performance.timeOrigin + startTime`. Bei LCP zählt der LETZTE Eintrag (der endgültige
// größte Inhalt), bei INP/CLS/TTFB der FRÜHESTE (Beginn der Interaktion bzw. der Navigation).
// Ohne Einträge bleibt nur die aktuelle Route als Ersatz.
//
// WERTE (E4): endlich, nicht negativ, höchstens VITAL_LIMITS; Millisekunden ganzzahlig, CLS
// auf vier Nachkommastellen. Was nicht passt, wird gar nicht erst gemeldet — ein einziges
// ungültiges Ereignis würde das ganze Bündel (bis 100 Ereignisse) kosten. Gesendet werden nur
// Name und Wert: kein rating, kein navigationType, keine URL, keine props.
//
// MELDEZEITPUNKT (E5): Standardverhalten ohne `reportAllChanges`. In dieser Einzelseiten-App
// heißt das: LCP und TTFB beschreiben nur das ECHTE Laden des Tabs (ein Routenwechsel lädt
// nichts neu), INP und CLS sammeln über das Tab-Leben und werden beim Ausblenden gemeldet.
// Das passt zum Versand: web-vitals hört `visibilitychange` im Capture-Modus am `window` und
// läuft damit VOR dem `document`-Listener der Laufzeit — die Messung liegt also schon im
// Puffer, wenn `flush('hide')` das Bündel baut.
import { onCLS, onINP, onLCP, onTTFB } from 'web-vitals';
import type { UsageEventInput } from './tracker';

export type VitalName = 'LCP' | 'INP' | 'CLS' | 'TTFB';

/** Obergrenzen des Backend-Schemas (apps/backend/src/services/usage/batchSchema.ts). */
export const VITAL_LIMITS: Record<VitalName, number> = { LCP: 600000, INP: 600000, TTFB: 600000, CLS: 100 };

const VITAL_NAMES: VitalName[] = ['LCP', 'INP', 'CLS', 'TTFB'];

/** CLS ist eine Verhältniszahl; vier Nachkommastellen reichen für Median und 95 %. */
const CLS_DECIMALS = 4;

/**
 * Der Ausschnitt aus dem `Metric` von web-vitals, den wir wirklich lesen. Bewusst schlank:
 * Tests brauchen so keine echten PerformanceEntry-Objekte, und es ist auf einen Blick zu
 * sehen, dass weder rating noch navigationType noch eine URL den Client verlassen.
 */
export interface VitalMetric {
  name: string;
  value: number;
  entries?: { startTime: number }[];
}

/** Die vier Melder von web-vitals — als eigene Schnittstelle, damit Tests sie ersetzen können. */
export interface VitalRegister {
  onLCP: (report: (metric: VitalMetric) => void) => void;
  onINP: (report: (metric: VitalMetric) => void) => void;
  onCLS: (report: (metric: VitalMetric) => void) => void;
  onTTFB: (report: (metric: VitalMetric) => void) => void;
}

export interface VitalContext {
  /** Welche Route galt zum Zeitpunkt der Messung? (tracker.routeAt) */
  routeAt: (ts: number) => string;
  /** performance.timeOrigin: Epoch-Millisekunde, ab der startTime zählt. */
  timeOrigin: number;
  /** Route für Messungen ohne Performance-Eintrag (z. B. CLS 0 ohne Layoutsprung). */
  fallbackRoute: string;
}

export interface WebVitalsBridge {
  /** Hängt den Handler an; beim ersten Aufruf werden die Beobachter registriert. */
  attach(handler: (metric: VitalMetric) => void): void;
  /** Löst nur den Handler — die Beobachter bleiben bis zum Ende der Seitenladung stehen. */
  detach(): void;
}

const isVitalName = (name: string): name is VitalName => VITAL_NAMES.indexOf(name as VitalName) >= 0;

/** Der Eintrag, dessen startTime den Messzeitpunkt bestimmt (E3). */
const leadingEntry = (name: VitalName, entries: { startTime: number }[]): { startTime: number } | null => {
  if (entries.length === 0) return null;
  if (name === 'LCP') return entries[entries.length - 1];
  return entries.reduce((earliest, entry) => (entry.startTime < earliest.startTime ? entry : earliest));
};

/**
 * Macht aus einer Messung ein vital-Ereignis — oder null, wenn sie nicht in den Vertrag passt.
 * Rein: keine Uhr, kein globales performance, nur die mitgegebenen Bausteine.
 */
export const vitalEventFrom = (metric: VitalMetric, context: VitalContext): UsageEventInput | null => {
  const { name, value: raw } = metric;
  if (typeof name !== 'string' || !isVitalName(name)) return null;

  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0 || raw > VITAL_LIMITS[name]) return null;
  const value = name === 'CLS' ? Number(raw.toFixed(CLS_DECIMALS)) : Math.round(raw);

  const entries = Array.isArray(metric.entries) ? metric.entries : [];
  const entry = leadingEntry(name, entries);
  const at = entry ? context.timeOrigin + entry.startTime : Number.NaN;
  const route = Number.isFinite(at) ? context.routeAt(at) : context.fallbackRoute;

  return { type: 'vital', name, value, route };
};

/** Die echten Melder des Pakets; die App benutzt sie, Tests setzen eigene ein. */
const webVitalsRegister: VitalRegister = { onLCP, onINP, onCLS, onTTFB };

export const createWebVitalsBridge = (register: VitalRegister = webVitalsRegister): WebVitalsBridge => {
  let handler: ((metric: VitalMetric) => void) | null = null;
  let registered = false;

  const report = (metric: VitalMetric): void => {
    try {
      if (handler) handler(metric);
    } catch {
      // Messwerte dürfen die Bedienung nie stören.
    }
  };

  return {
    attach(next: (metric: VitalMetric) => void): void {
      handler = next;
      if (registered) return;
      // Auch ein gescheiterter Versuch gilt als erledigt: ein zweiter Anlauf brächte nur die
      // gepufferten Einträge doppelt.
      registered = true;
      try {
        register.onLCP(report);
        register.onINP(report);
        register.onCLS(report);
        register.onTTFB(report);
      } catch {
        // Browser ohne PerformanceObserver: keine Ladeleistung, alles andere läuft weiter.
      }
    },
    detach(): void {
      handler = null;
    },
  };
};

/** Die eine Brücke der App — ein Beobachtersatz je Seitenladung. Tests bauen eigene. */
export const webVitalsBridge: WebVitalsBridge = createWebVitalsBridge();
